// src/components/TradeTab.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Flame, Trophy, Lock, DollarSign, ArrowUp, ArrowDown, Coins } from 'lucide-react';
import TradingChart from './TradingChart';

const coins = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT'];
const quickAmounts = [5, 10, 15, 20, 100];

const CustomCoinIcon = ({ className = "text-amber-400", size = 18 }) => (
  <svg 
    className={className}
    width={size}
    height={size}
    viewBox="0 0 512 512" 
    fill="currentColor"
  >
    <g transform="translate(0.000000,512.000000) scale(0.100000,-0.100000)" stroke="none">
      <path d="M4101 4259 c-37 -15 -50 -39 -51 -91 l0 -48 -1567 -2 -1568 -3 -48 -22 -49 -23 -32 25 c-47 36 -112 56 -184 58 -58 2 -66 0 -88 -26 -48 -56 -14 -137 58 -137 47 0 108 -19 114 -35 3 -7 -2 -27 -10 -43 -13 -24 -136 -528 -136 -554 0 -5 934 -8 2075 -8 l2075 0 0 354 c0 195 -3 362 -6 371 -15 38 -55 45 -264 45 l-200 0 0 29 c0 47 -21 91 -51 106 -32 17 -35 17 -68 4z"/>
      <path d="M500 3172 c0 -22 37 -55 94 -82 173 -82 266 -230 266 -420 0 -112 -18 -196 -77 -348 -129 -336 -269 -712 -309 -828 -37 -108 -44 -143 -44 -205 0 -215 153 -394 367 -429 83 -14 669 -13 735 1 114 25 222 105 271 202 21 40 53 175 129 528 55 261 105 478 110 484 5 5 142 12 321 16 333 7 349 9 458 64 76 39 178 145 218 226 23 48 44 125 79 290 l47 224 615 5 c338 3 616 6 617 8 1 1 11 12 23 24 13 14 20 35 20 60 l0 38 100 0 c94 0 103 2 125 25 20 19 25 34 25 75 l0 50 -2095 0 c-1152 0 -2095 -3 -2095 -8z m1822 -366 c9 -134 75 -253 169 -301 56 -29 112 -1 125 63 6 28 1 37 -41 79 -63 65 -77 95 -83 181 l-5 72 252 0 c138 0 251 -2 251 -5 0 -40 -87 -403 -105 -441 -32 -65 -99 -128 -173 -162 -61 -28 -70 -28 -439 -35 -172 -4 -193 -2 -193 12 0 9 25 131 55 271 30 140 55 258 55 262 0 28 101 105 121 92 3 -2 9 -42 11 -88z"/>
    </g>
  </svg>
);

const TradeTab = ({
  user,
  selectedCrypto,
  setSelectedCrypto,
  balanceType,      // 👈 'USDT' یا 'COIN'
  setBalanceType,   // 👈 تغییر نوع بالانس
  currentPrice,
  currentRound,
  isFrozen,
  timeLeft,
  selectedPosition,
  setSelectedPosition,
  activeBet,
  betAmount,
  setBetAmount,
  loadingBet,
  onStartDuel,
  priceHistory = [],
}) => {
  const { t } = useTranslation();

  const formatTime = (seconds) => {
    if (seconds <= 0) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const rawTarget = currentRound?.open_price ?? currentRound?.target_price;
  const decimals = selectedCrypto === 'BTCUSDT' ? 2 : selectedCrypto === 'ETHUSDT' ? 2 : 4;
  const formattedTarget = rawTarget && !isNaN(parseFloat(rawTarget))
    ? parseFloat(rawTarget).toFixed(decimals)
    : '0.00';

  const totalUp = parseFloat(currentRound?.total_up || 0);
  const totalDown = parseFloat(currentRound?.total_down || 0);
  const totalVolume = totalUp + totalDown;

  const upPercentage = totalVolume > 0 ? Math.round((totalUp / totalVolume) * 100) : 50;
  const downPercentage = totalVolume > 0 ? 100 - upPercentage : 50;

  const isLocked = isFrozen || Boolean(activeBet);
  const currentBet = activeBet;

  const currentBalance = balanceType === 'USDT'
    ? parseFloat(user?.balance || 0)
    : parseFloat(user?.coins || 0);

  return (
    <main className="p-4 flex-1 flex flex-col gap-4">
      {/* User Stats Summary */}
      <div className="grid grid-cols-3 gap-2 bg-[#1A1528] p-2.5 rounded-2xl border border-white/5 text-center text-xs">
        <div className="flex items-center justify-center gap-1 font-bold text-amber-400">
          <CustomCoinIcon size={14} className="text-amber-400" />
          <span>{user?.coins || 0}</span>
        </div>
        <div className="flex items-center justify-center gap-1 font-bold text-orange-400 border-x border-white/5">
          <Flame size={14} fill="currentColor" /> {t('streak')} {user?.streak || 0}
        </div>
        <div className="flex items-center justify-center gap-1 font-bold text-purple-400">
          <Trophy size={14} /> {user?.wins || 0} {t('wins')}
        </div>
      </div>

      {/* Crypto Selector */}
      <div className="grid grid-cols-3 gap-2 bg-[#1A1528] p-1.5 rounded-2xl border border-white/5">
        {coins.map((coin) => (
          <button
            key={coin}
            onClick={() => setSelectedCrypto(coin)}
            className={`py-2 text-xs font-bold rounded-xl transition-all ${
              selectedCrypto === coin
                ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/40'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {coin.replace('USDT', '')}
          </button>
        ))}
      </div>

      {/* Price & Timer Info */}
      <div className="flex items-baseline justify-between px-2 pt-2">
        <div>
          <div className="text-3xl font-extrabold font-mono tracking-tight text-white">
            ${currentPrice}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            {t('target')} ${formattedTarget}
          </div>
        </div>

        <div className="flex flex-col items-end gap-1">
          <div className={`text-3xl font-extrabold font-mono tracking-wider ${isFrozen ? 'text-rose-500' : 'text-purple-400'}`}>
            {isFrozen ? t('frozen') : formatTime(timeLeft)}
          </div>
          {isFrozen && (
            <span className="flex items-center gap-1 text-[10px] font-bold bg-rose-500/10 text-rose-400 px-2 py-0.5 rounded-md border border-rose-500/20 animate-pulse">
              <Lock size={11} /> {t('locked30s')}
            </span>
          )}
        </div>
      </div>

      {/* Chart */}
      <TradingChart
        currentPrice={currentPrice}
        openPrice={rawTarget}
        priceHistory={priceHistory}
        selectedCrypto={selectedCrypto}
      />

      {/* Sentiment Bar */}
      <div className="bg-[#1A1528] p-3 rounded-2xl border border-white/5 flex flex-col gap-2">
        <div className="flex justify-between items-center text-xs font-bold px-1">
          <span className="text-emerald-400 flex items-center gap-1">
            {t('up')} {upPercentage}%
          </span>
          <span className="text-gray-400 text-[10px] font-normal">
            {t('pool')}: ${totalVolume.toFixed(0)}
          </span>
          <span className="text-rose-400 flex items-center gap-1">
            {downPercentage}% {t('down')}
          </span>
        </div>

        <div className="w-full h-2.5 bg-[#120F1D] rounded-full overflow-hidden flex p-0.5 border border-white/5">
          <div
            className="h-full bg-emerald-500 rounded-l-full transition-all duration-500 ease-out"
            style={{ width: `${upPercentage}%` }}
          />
          <div
            className="h-full bg-rose-500 rounded-r-full transition-all duration-500 ease-out"
            style={{ width: `${downPercentage}%` }}
          />
        </div>
      </div>

      {/* Amount & Balance Selector */}
      <div className="bg-[#1A1528] p-3 rounded-2xl border border-white/5 flex flex-col gap-2.5">
        {/* سوئیچ USDT / COIN */}
        <div className="flex justify-between items-center bg-[#120F1D] p-1 rounded-xl border border-white/5">
          <button
            onClick={() => setBalanceType('USDT')}
            className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1 ${
              balanceType === 'USDT'
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <DollarSign size={13} /> USDT
          </button>
          <button
            onClick={() => setBalanceType('COIN')}
            className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1 ${
              balanceType === 'COIN'
                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <CustomCoinIcon size={14} className="text-amber-400" /> COIN
          </button>
        </div>

        <div className="flex justify-between items-center text-xs text-gray-400 px-1">
          <span>{t('amount')}</span>
          <span>
            {t('balance')}:{' '}
            <strong className={balanceType === 'USDT' ? 'text-emerald-400' : 'text-amber-400'}>
              {balanceType === 'USDT' ? `$${currentBalance.toFixed(2)}` : `${currentBalance.toLocaleString()} COIN`}
            </strong>
          </span>
        </div>

        <div className="relative flex items-center">
          {balanceType === 'USDT' ? (
            <DollarSign className="absolute left-3 text-emerald-400" size={18} />
          ) : (
            <CustomCoinIcon className="absolute left-3 text-amber-400" size={18} />
          )}
          <input
            type="number"
            disabled={isFrozen}
            value={betAmount}
            onChange={(e) => setBetAmount(Math.max(1, parseFloat(e.target.value) || 0))}
            className="w-full bg-[#120F1D] border border-white/10 rounded-xl py-2.5 pl-9 pr-4 font-mono font-bold text-white text-base focus:outline-none focus:border-purple-500 transition-all disabled:opacity-50"
            placeholder={t('amount')}
          />
        </div>

        <div className="grid grid-cols-5 gap-1.5 pt-0.5">
          {quickAmounts.map((amt) => (
            <button
              key={amt}
              disabled={isFrozen}
              onClick={() => setBetAmount(amt)}
              className={`py-1.5 rounded-lg text-xs font-bold transition-all border ${
                betAmount === amt
                  ? balanceType === 'USDT'
                    ? 'bg-emerald-600/30 text-emerald-300 border-emerald-500/50'
                    : 'bg-amber-600/30 text-amber-300 border-amber-500/50'
                  : 'bg-[#120F1D] text-gray-400 border-white/5 hover:border-white/20'
              } disabled:opacity-50`}
            >
              {balanceType === 'USDT' ? `$${amt}` : amt}
            </button>
          ))}
        </div>
      </div>

      {/* Position Buttons */}
      <div className="grid grid-cols-2 gap-3">
        {/* دکمه UP */}
        <button
          disabled={isFrozen || currentBet === 'DOWN'}
          onClick={() => setSelectedPosition('UP')}
          className={`py-3.5 rounded-2xl font-extrabold text-sm flex items-center justify-center gap-1.5 transition-all ${
            selectedPosition === 'UP'
              ? 'bg-emerald-500 text-black shadow-lg shadow-emerald-500/40 scale-[1.02]'
              : 'bg-[#1E2B25] text-emerald-400 border border-emerald-500/30'
          } ${(isFrozen || currentBet === 'DOWN') ? 'opacity-40 cursor-not-allowed' : ''}`}
        >
          <ArrowUp size={16} /> {t('up')}
        </button>

        {/* دکمه DOWN */}
        <button
          disabled={isFrozen || currentBet === 'UP'}
          onClick={() => setSelectedPosition('DOWN')}
          className={`py-3.5 rounded-2xl font-extrabold text-sm flex items-center justify-center gap-1.5 transition-all ${
            selectedPosition === 'DOWN'
              ? 'bg-rose-500 text-white shadow-lg shadow-rose-500/40 scale-[1.02]'
              : 'bg-[#2B1E24] text-rose-400 border border-rose-500/30'
          } ${(isFrozen || currentBet === 'UP') ? 'opacity-40 cursor-not-allowed' : ''}`}
        >
          <ArrowDown size={16} /> {t('down')}
        </button>
      </div>

      {/* Action Button */}
      <button
        onClick={onStartDuel}
        disabled={!selectedPosition || loadingBet || isFrozen || betAmount > currentBalance}
        className="w-full py-4 bg-purple-600 hover:bg-purple-500 active:scale-[0.98] disabled:opacity-40 text-white font-extrabold rounded-2xl shadow-xl shadow-purple-600/30 transition-all text-base mt-1"
      >
        {loadingBet
          ? t('processing')
          : isFrozen
          ? t('locked')
          : betAmount > currentBalance
          ? t('insufficientBalance')
          : activeBet
          ? `${t('startDuel')} +${balanceType === 'USDT' ? `$${betAmount}` : `${betAmount} COIN`} (${activeBet})`
          : `${t('startDuel')} (${balanceType === 'USDT' ? `$${betAmount}` : `${betAmount} COIN`})`}
      </button>
    </main>
  );
};

export default TradeTab;